import React, { useEffect, useState, useRef } from 'react'
import { useParams } from 'react-router-dom'
import useResource from '../custom-hooks/useResource'
import SEO from './../utils/SEOMetaTags'
import { 
    TabbedContent, 
    ShowAboutLayout, 
    BasicGridModule, 
    PageHero, 
    Loading, 
    SocialShareBlock } from '@scrippsproduct/scripps-external-component-library'
import PageNotFound from './PageNotFound'

import './../scss/ShowDetails.scss'
import EpisodeGuide from './partials/EpisodeGuide'
import ShowUpcomingWrapper from '../utils/ShowUpcomingWrapper'

const NETWORK = process.env.REACT_APP_NETWORK
const SCHEDULE_ENDPOINT = process.env.REACT_APP_SCHEDULE_V2

export default function ShowDetails() {
    const urlParams = useParams()
    const seriesList = useResource({
        cacheName: `showDetails:${urlParams.slug}`,
        resourceName: 'showDetails', 
        resourceId: urlParams.slug, 
        queryVars: {id: `slug = ${urlParams.slug}`}
    })
    
    const rootEl = document.querySelector(':root')
    const [show, setShow] = useState(null)
    const tabs = useRef([])

    useEffect(() => {
        if (seriesList?.data !== undefined && seriesList?.isSuccess && seriesList?.data?.SeriesList?.length) {
            setShow(seriesList.data.SeriesList[0])
            // don't show cast tab if there aren't any cast members
            if (seriesList.data.SeriesList[0].cast.length) {
                tabs.current = [
                    {
                        button: {
                            gradientEndColor: seriesList.data.SeriesList[0].secondary_color,
                            gradientStartColor: seriesList.data.SeriesList[0].primary_color,
                            buttonBgColor: `#fff`,
                            buttonHoverBgColor: seriesList.data.SeriesList[0].secondary_color,
                            buttonTextColor: seriesList.data.SeriesList[0].secondary_color,
                            label: 'Episodes',
                            buttonHoverTextColor: `#fff`,
                        },
                        content: <EpisodeGuide slug={seriesList.data.SeriesList[0].slug} />,
                        classname: 'episodes-guide-tab',
                    },
                    { 
                        button: {
                            gradientEndColor: seriesList.data.SeriesList[0].secondary_color,
                            gradientStartColor: seriesList.data.SeriesList[0].primary_color,
                            buttonBgColor: `#fff`,
                            buttonHoverBgColor: seriesList.data.SeriesList[0].secondary_color,
                            buttonTextColor: seriesList.data.SeriesList[0].secondary_color,
                            label: 'About',
                            buttonHoverTextColor: `#fff`,
                        },
                        content: <ShowAboutLayout series={
                            {
                                title: seriesList.data.SeriesList[0].title,
                                series_synopsis: seriesList.data.SeriesList[0].series_synopsis,
                                cast: seriesList.data.SeriesList[0].cast,
                                creators: seriesList.data.SeriesList[0]?.creators,
                                public_content: seriesList.data.SeriesList[0]?.public_content,
                                detailImage: seriesList.data.SeriesList[0]?.detailImage ?? {url: '', aspectRatio: 1.6095238095238096},
                                genres: seriesList.data.SeriesList[0]?.genres,
                                rating: seriesList.data.SeriesList[0]?.rating,
                                element_time: seriesList.data.SeriesList[0].element_time,            
                            }
                        } />,
                        classname: 'show-about-tab',
                    },
                    {
                        button: {
                            gradientEndColor: seriesList.data.SeriesList[0].secondary_color,
                            gradientStartColor: seriesList.data.SeriesList[0].primary_color,
                            buttonBgColor: `#fff`,
                            buttonHoverBgColor: seriesList.data.SeriesList[0].secondary_color,
                            buttonTextColor: seriesList.data.SeriesList[0].secondary_color,
                            label: `Cast`,
                            buttonHoverTextColor: `#fff`,
                        },
                        content: <BasicGridModule
                            itemsPerRow={4}
                            classname={`episodes-grid`}
                            gap={`3rem 2.5rem`}
                            title={`The ${seriesList.data.SeriesList[0].title} Cast`}
                            itemComponent={{
                                layout: `ImageCard`
                            }}
                            infoPanelComponent={{
                                layout: `CastInfoLayout`,
                                backButtonLabel: `Back to Full Cast`, 
                                resourceList: seriesList.data.SeriesList[0].cast,
                                classname: 'cast-details-panel',
                            }}                                
                            items={seriesList.data.SeriesList[0].cast.map(item => (
                                { 
                                    id: item.id,
                                    image: { 
                                        url: item.image.url, 
                                        altText: `${item.real_name} as ${item.name}`,
                                        aspectRatio: item.image.aspectRatio,
                                    },
                                    title: item.real_name,
                                    content: item.name,
                                    facebookHandle: item.facebookHandle,
                                    twitterHandle: item.twitterHandle,
                                    instagramHandle: item.instagramHandle,
                                    cta: {
                                        label: `View Biography`,
                                        type: 'info-panel',
                                        href: '',
                                        relatedResourceId: item.id,
                                        position: 'end',
                                    },
                                    clampContentLines: false,    
                                }
                            ))} 
                        />,
                        classname: 'cast-list-tab',
                    },
                    {
                        button: {
                            gradientEndColor: seriesList.data.SeriesList[0].secondary_color,
                            gradientStartColor: seriesList.data.SeriesList[0].primary_color,
                            buttonBgColor: `#fff`,
                            buttonHoverBgColor: seriesList.data.SeriesList[0].secondary_color,
                            buttonTextColor: seriesList.data.SeriesList[0].secondary_color,
                            label: 'Upcoming',
                            buttonHoverTextColor: `#fff`,
                        },
                        content: <ShowUpcomingWrapper seriesPrefix={seriesList.data.SeriesList[0].seriesPrefix} showTitle={seriesList.data.SeriesList[0].title} />,
                        classname: 'upcoming-tab',
                    },
                ]
            } else {
                tabs.current = [
                    {
                        button: {
                            gradientEndColor: seriesList.data.SeriesList[0].secondary_color,
                            gradientStartColor: seriesList.data.SeriesList[0].primary_color,
                            buttonBgColor: `#fff`,
                            buttonHoverBgColor: seriesList.data.SeriesList[0].secondary_color,
                            buttonTextColor: seriesList.data.SeriesList[0].secondary_color,
                            label: 'Episodes',
                            buttonHoverTextColor: `#fff`,
                        },
                        content: <EpisodeGuide slug={seriesList.data.SeriesList[0].slug} />,
                        classname: 'episodes-guide-tab',
                    },
                    { 
                        button: {
                            gradientEndColor: seriesList.data.SeriesList[0].secondary_color,
                            gradientStartColor: seriesList.data.SeriesList[0].primary_color,
                            buttonBgColor: `#fff`,
                            buttonHoverBgColor: seriesList.data.SeriesList[0].secondary_color,
                            buttonTextColor: seriesList.data.SeriesList[0].secondary_color,
                            label: 'About',
                            buttonHoverTextColor: `#fff`,
                        },
                        content: <ShowAboutLayout series={
                            {
                                title: seriesList.data.SeriesList[0].title,
                                series_synopsis: seriesList.data.SeriesList[0].series_synopsis,
                                cast: seriesList.data.SeriesList[0].cast,
                                creators: seriesList.data.SeriesList[0]?.creators,
                                public_content: seriesList.data.SeriesList[0]?.public_content,
                                detailImage: seriesList.data.SeriesList[0]?.detailImage,
                                genres: seriesList.data.SeriesList[0]?.genres,
                                rating: seriesList.data.SeriesList[0]?.rating,
                                element_time: seriesList.data.SeriesList[0].element_time,            
                            }
                        } />,
                        classname: 'show-about-tab',
                    },
                    {
                        button: {
                            gradientEndColor: seriesList.data.SeriesList[0].secondary_color,
                            gradientStartColor: seriesList.data.SeriesList[0].primary_color,
                            buttonBgColor: `#fff`,
                            buttonHoverBgColor: seriesList.data.SeriesList[0].secondary_color,
                            buttonTextColor: seriesList.data.SeriesList[0].secondary_color,
                            label: 'Upcoming',
                            buttonHoverTextColor: `#fff`,
                        },
                        content: <ShowUpcomingWrapper seriesPrefix={seriesList.data.SeriesList[0].seriesPrefix} showTitle={seriesList.data.SeriesList[0].title} />,
                        classname: 'upcoming-tab',
                    },
                ]
            }
        }
    }, [seriesList.isSuccess, seriesList.data])
    
    if ((seriesList.isSuccess && show === null) || seriesList.isError) return (
        <section className="info-page">
            <content-constrainer>
                <PageNotFound message={`We can't find the show ${urlParams.slug.replace('-', ' ').toUpperCase()}.`} />
            </content-constrainer>
        </section>
    )

    if (seriesList.isSuccess && show !== null) {
        rootEl.style.setProperty('--body-radial-gradient', `radial-gradient(ellipse at top left, ${show.secondary_color} 0%,  ${show.secondary_color} 50%, ${show.primary_color} 90%)`)
        rootEl.style.setProperty('--footer-gradient', `linear-gradient(180deg, ${show.primary_color}77 0%, ${show.secondary_color}88 100%)`)
        rootEl.style.setProperty('--cta-button-color', show.primary_color)
        const pia = setInterval(() => {
            if (document.querySelector('tabbed-content')) {
                document.querySelector('tabbed-content')?.style.setProperty('--tabbed-content-border-color', `${show.secondary_color}`)
                clearInterval(pia)
            }
        }, 100)        
    }

    return (<show-details-page>
        { (seriesList.isSuccess && show !== null) && <SEO title={`${show.title.replace(/<br\s*\/?>/i, ' ')} | Show | Laff`} /> }
        {(seriesList.isSuccess && show !== null) && 
            <content-constrainer class="content-constrainer--no-padding content-constrainer--lead-container show-hero-constrainer">
                <PageHero 
                    layoutComponent='IONShowHero'
                    layoutProps={{
                        show: {
                            title: show.title,
                            nextProgram: show.nextProgram,
                            bannerImage: show.bannerImage,
                            bannerMobileImage: show.bannerMobileImage,
                            seriesPrefix: show.seriesPrefix,
                            bannerImagePosition: show.bannerImagePosition,
                            bannerMobileImagePosition: show.bannerMobileImagePosition,
                            logoImage: show.logoImage,
                            heroContentAlignment: show.bannerTextAlignment,
                        },
                        NETWORK,
                        SCHEDULE_ENDPOINT,
                    }}
                /> 
            </content-constrainer>
        }
        <section className="page-section">
            <content-constrainer class="content-constrainer--no-padding content-constrainer--no-border">
                { (show !== null && seriesList.isSuccess) && 
                    <TabbedContent 
                        navRightSideContent={<div style={{'--share-btn-color': show.primary_color}}>
                            <SocialShareBlock url={window.location.href} text={`${show.title} on ION`} longtext={`${show.title} on ION`} />
                        </div>} 
                        tabs={tabs.current}
                    /> 
                }
                { (seriesList.isLoading) && <Loading topOffset={`10rem`} spinnerColor={`hsl(0deg 0% 86.68% / 77%)`} />}
            </content-constrainer>
        </section>
    </show-details-page>)
}
